import { formatSecondsString } from '../../util/date';

export type UtilizationMetric =
  | 'activePatients'
  | 'newActivePatients'
  | 'sessions'
  | 'sessionsPerPatient'
  | 'duration'
  | 'totalTimeTaken'
  | 'totalReps'
  | 'repsPerPatient'
  | 'repsPerSession'
  | 'activityCount';

export type SumField = 'duration' | 'totalTimeTaken' | 'totalReps' | 'activityCount';

export type MetricPart = 'sessionCount' | 'distinctPatients' | 'newPatients' | { sum: SumField };

interface BaseMetricConfig {
  label: string;
  format: (value: number) => string;
  ragDefaults?: { red: number; amber: number };
  sliderRange?: { min: number; max: number; step: number };
  columnWidthClass: string;
}

export type UtilizationMetricConfig = BaseMetricConfig & {
  numerator: MetricPart;
  denominator?: MetricPart;
};

const NARROW_COLUMN = 'min-w-20';
const WIDE_COLUMN = 'min-w-32';

const formatInteger = (value: number) => Math.round(value).toLocaleString();
const formatDecimal = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 1 });

export const UTILIZATION_METRICS: Record<UtilizationMetric, UtilizationMetricConfig> = {
  activePatients: {
    label: 'Active patients',
    numerator: 'distinctPatients',
    format: formatInteger,
    ragDefaults: { red: 3, amber: 6 },
    sliderRange: { min: 0, max: 20, step: 1 },
    columnWidthClass: NARROW_COLUMN
  },
  newActivePatients: {
    label: 'New active patients',
    numerator: 'newPatients',
    format: formatInteger,
    ragDefaults: { red: 3, amber: 6 },
    sliderRange: { min: 0, max: 20, step: 1 },
    columnWidthClass: NARROW_COLUMN
  },
  sessions: {
    label: 'Sessions',
    numerator: 'sessionCount',
    format: formatInteger,
    columnWidthClass: NARROW_COLUMN
  },
  sessionsPerPatient: {
    label: 'Sessions per patient',
    numerator: 'sessionCount',
    denominator: 'distinctPatients',
    format: formatDecimal,
    columnWidthClass: NARROW_COLUMN
  },
  duration: {
    label: 'Duration',
    numerator: { sum: 'duration' },
    format: formatSecondsString,
    columnWidthClass: WIDE_COLUMN
  },
  totalTimeTaken: {
    label: 'Time on task',
    numerator: { sum: 'totalTimeTaken' },
    format: formatSecondsString,
    columnWidthClass: WIDE_COLUMN
  },
  totalReps: {
    label: 'Reps',
    numerator: { sum: 'totalReps' },
    format: formatInteger,
    columnWidthClass: NARROW_COLUMN
  },
  repsPerPatient: {
    label: 'Reps per patient',
    numerator: { sum: 'totalReps' },
    denominator: 'distinctPatients',
    format: formatDecimal,
    columnWidthClass: NARROW_COLUMN
  },
  repsPerSession: {
    label: 'Reps per session',
    numerator: { sum: 'totalReps' },
    denominator: 'sessionCount',
    format: formatDecimal,
    columnWidthClass: NARROW_COLUMN
  },
  activityCount: {
    label: 'Activities',
    numerator: { sum: 'activityCount' },
    format: formatInteger,
    columnWidthClass: NARROW_COLUMN
  }
};
